import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Wrench, Warehouse, Activity, LayoutDashboard, Monitor, Handshake } from "lucide-react";
import { DecorativeLines } from "@/components/DecorativeLines";

const ServiceCard = ({ service, index }) => {
  const IconComponent = service.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
    >
      <Card
        className="service-card h-full p-6 bg-white dark:bg-[#121418] border-gray-200 dark:border-white/5 rounded-xl shadow-sm dark:shadow-none"
        data-testid={`services-card-${service.key}`}
      >
        <div className="w-12 h-12 rounded-lg bg-[#EA8628]/10 flex items-center justify-center">
          <IconComponent className="w-6 h-6 text-[#EA8628]" />
        </div>
        <h3 className="mt-5 text-xl font-semibold text-[#212738] dark:text-white">
          {service.title}
        </h3>
        <p className="mt-3 text-[#504D4D] dark:text-white/60 text-sm leading-relaxed">
          {service.description}
        </p>
        <ul className="mt-4 space-y-2">
          <li className="flex items-center gap-2 text-sm text-[#504D4D] dark:text-white/70">
            <span className="w-1.5 h-1.5 rounded-full bg-[#EA8628]" />
            {service.features[0]}
          </li>
          <li className="flex items-center gap-2 text-sm text-[#504D4D] dark:text-white/70">
            <span className="w-1.5 h-1.5 rounded-full bg-[#EA8628]" />
            {service.features[1]}
          </li>
          <li className="flex items-center gap-2 text-sm text-[#504D4D] dark:text-white/70">
            <span className="w-1.5 h-1.5 rounded-full bg-[#EA8628]" />
            {service.features[2]}
          </li>
        </ul>
        <a
          href={service.href || "#kontakt"}
          {...(service.href ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="mt-6 inline-flex items-center text-[#EA8628] hover:text-[#ff9734] transition-colors duration-200 text-sm font-medium"
          data-testid={`services-card-cta-${service.key}`}
        >
          Dowiedz się więcej →
        </a>
      </Card>
    </motion.div>
  );
};

const services = [
  {
    key: "serwis",
    icon: Wrench,
    title: "Serwis",
    description: "Zapewniamy kompleksowe naprawy oraz przeglądy techniczne sprzętu medycznego, gwarantując bezpieczeństwo użytkowania i ciągłość pracy urządzeń.",
    features: ["Doświadczony zespół serwisowy", "Zgodność z normami i dokumentacją producenta", "Sprawna realizacja i transparentność procesu"],
  },
  {
    key: "logistyka",
    icon: Warehouse,
    title: "Zaplecze logistyczno-magazynowe",
    description: "Dysponujemy nowoczesnym zapleczem logistyczno-magazynowym, które zapewnia sprawną i bezpieczną obsługę sprzętu medycznego.",
    features: ["Bezpieczne magazynowanie", "Sprawna logistyka operacyjna", "Obsługa urządzeń zastępczych i części"],
  },
  {
    key: "quantidose",
    icon: Activity,
    title: "Quantidose",
    description: "QuantiDose to rozwiązanie do monitorowania i archiwizowania dawki promieniowania jonizującego.",
    features: ["Integracja z systemem PACS", "Zaawansowany moduł statystyk i analiz", "Audyt i generowanie raportów pacjenta"],
    href: "https://quantidose.ckkresq.pl/",
  },
  {
    key: "irequest",
    icon: LayoutDashboard,
    title: "iReQuest",
    description: "iReQuest to nowoczesny system CRM zaprojektowany z myślą o efektywnym zarządzaniu procesami serwisowymi i logistycznymi w branży medycznej.",
    features: ["Pełna kontrola nad zgłoszeniami i raportami serwisowymi", "Proste zarządzanie danymi kontrahentów", "Historia czynności serwisowych"],
  },
  {
    key: "wsparcie-it",
    icon: Monitor,
    title: "Wsparcie IT",
    description: "Zapewniamy kompleksowe wsparcie IT, które usprawnia procesy serwisowe, logistyczne oraz operacyjne w branży medycznej.",
    features: ["Dedykowane rozwiązania technologiczne", "Integracja i automatyzacja procesów", "Bezpieczeństwo i stabilność systemów"],
  },
  {
    key: "oem",
    icon: Handshake,
    title: "Współpraca OEM",
    description: "Oferujemy możliwość współpracy w modelu OEM, działając jako zaufane przedłużenie struktur producenta w obszarze serwisu.",
    features: ["Realizacja usług pod marką producenta", "Elastyczny model podwykonawczy", "Wzmocnienie obsługi posprzedażowej"],
  },
];

export const Services = () => {
  return (
    <section
      id="uslugi"
      className="section-spacing bg-[#F7F9F9] dark:bg-[#0D0F12] relative"
      aria-label="Usługi serwisowe"
      data-testid="services-section"
    >
      <DecorativeLines variant="circles" />
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="text-[#EA8628] font-semibold text-sm uppercase tracking-wider">
            Oferta
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#212738] dark:text-white">
            Co oferujemy?
          </h2>
          <p className="mt-4 text-[#504D4D] dark:text-white/70">
            Kompleksowy serwis urządzeń medycznych dla szpitali, klinik i przychodni.
          </p>
        </motion.div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={service.key} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
